import { Link } from "@tanstack/react-router";
import { format } from "date-fns";

import { useRiskDetail, useUpdateRisk } from "@/application/use-cases/risks/use-risks.queries";
import { useMeetingsForEntity } from "@/application/use-cases/meetings/use-meetings.queries";
import { useStakeholdersList } from "@/application/use-cases/stakeholders/use-stakeholders.queries";
import { StatusDot, STATUS_LABEL } from "@/presentation/components/status/status-dot";
import { WeekSelect } from "@/presentation/components/weeks/week-select";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";
import { Label } from "@monorepo-apps/ui/src/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import type { Rag, Severity, Status } from "@/infrastructure/supabase/database.types";

export function RiskDrawerContent({ id }: { id: string }) {
  const riskQ = useRiskDetail(id);
  const meetingsQ = useMeetingsForEntity("risk", id);
  const stakeholdersQ = useStakeholdersList();
  const updateRisk = useUpdateRisk();

  if (!riskQ.data) {
    return <p className="text-muted-foreground px-6 py-8 text-xs italic">Loading…</p>;
  }
  const r = riskQ.data;
  const stakeholders = stakeholdersQ.data ?? [];
  const dueDateInputValue = r.due_date ? new Date(r.due_date).toISOString().slice(0, 10) : "";

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="border-border/60 flex flex-col gap-1.5 border-b px-6 py-5">
        <span className="eyebrow inline-flex items-center gap-2">
          <span className="font-mono">{r.code}</span>
          <span className="text-border">·</span>
          <StatusDot tone={r.rag} /> RAG {r.rag}
          <span className="text-border">·</span>
          <StatusDot tone={r.status} /> {STATUS_LABEL[r.status]}
        </span>
        <h2 className="text-base font-medium tracking-tight">{r.title}</h2>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-[120px_1fr] gap-4">
            <Field label="Code">
              <Input
                defaultValue={r.code}
                aria-label="Risk code"
                className="font-data uppercase tabular-nums"
                onBlur={(e) => {
                  const next = e.target.value.trim().toUpperCase();
                  if (next && next !== r.code) {
                    updateRisk.mutate({ id: r.id, patch: { code: next } });
                  } else {
                    e.target.value = r.code;
                  }
                }}
              />
            </Field>
            <Field label="Title">
              <Input
                defaultValue={r.title}
                onBlur={(e) =>
                  e.target.value !== r.title &&
                  e.target.value.trim() &&
                  updateRisk.mutate({ id: r.id, patch: { title: e.target.value.trim() } })
                }
              />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Field label="RAG">
              <Select
                value={r.rag}
                onValueChange={(v) => updateRisk.mutate({ id: r.id, patch: { rag: v as Rag } })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="green">Green</SelectItem>
                  <SelectItem value="amber">Amber</SelectItem>
                  <SelectItem value="red">Red</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Severity">
              <Select
                value={r.severity}
                onValueChange={(v) =>
                  updateRisk.mutate({ id: r.id, patch: { severity: v as Severity } })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select
                value={r.status}
                onValueChange={(v) =>
                  updateRisk.mutate({ id: r.id, patch: { status: v as Status } })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Owner">
              <Select
                value={r.owner_id ?? "__none__"}
                onValueChange={(v) =>
                  updateRisk.mutate({
                    id: r.id,
                    patch: { owner_id: v === "__none__" ? null : v },
                  })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unassigned</SelectItem>
                  {stakeholders.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}{" "}
                      <span className="text-muted-foreground text-[10px] uppercase">· {s.org}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Due date">
              <Input
                type="date"
                defaultValue={dueDateInputValue}
                className="font-data tabular-nums"
                onBlur={(e) => {
                  const next = e.target.value ? new Date(e.target.value).toISOString() : null;
                  if (next !== r.due_date) {
                    updateRisk.mutate({ id: r.id, patch: { due_date: next } });
                  }
                }}
              />
            </Field>
          </div>

          <Field label="Week">
            <WeekSelect
              value={r.phase_id}
              onChange={(next) => {
                if (next !== r.phase_id) {
                  updateRisk.mutate({ id: r.id, patch: { phase_id: next } });
                }
              }}
              ariaLabel="Risk week"
            />
          </Field>

          <Field label="Description">
            <Textarea
              defaultValue={r.description ?? ""}
              rows={4}
              onBlur={(e) =>
                e.target.value !== (r.description ?? "") &&
                updateRisk.mutate({ id: r.id, patch: { description: e.target.value } })
              }
            />
          </Field>

          <Field label="Mitigation">
            <Textarea
              defaultValue={r.mitigation ?? ""}
              rows={4}
              onBlur={(e) => {
                if (e.target.value !== (r.mitigation ?? "")) {
                  updateRisk.mutate({ id: r.id, patch: { mitigation: e.target.value } });
                }
              }}
            />
          </Field>

          <Field label="Discussed in">
            {meetingsQ.data && meetingsQ.data.length > 0 ? (
              <ul className="flex flex-col gap-1 text-sm">
                {meetingsQ.data.map((m) => (
                  <li key={m.id}>
                    <Link to="/meetings/$id" params={{ id: m.id }} className="hover:underline">
                      <span className="text-muted-foreground text-[11px]">
                        {format(new Date(m.meeting_at), "d MMM")} ·{" "}
                      </span>
                      {m.title}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-xs italic">
                Not yet discussed in any logged meeting.
              </p>
            )}
          </Field>

          <p className="text-muted-foreground border-border/40 font-data mt-2 border-t pt-3 text-[10px] tracking-[0.16em] tabular-nums">
            Last updated {format(new Date(r.updated_at), "d MMM, HH:mm")}
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-muted-foreground font-data text-[10px] tracking-[0.18em] uppercase">
        {label}
      </Label>
      {children}
    </div>
  );
}
